/**
 * Holiday calls. M1: my year, someone else's year (managers), bank holidays, the branch
 * calendar and the import disagreement list. M2: the approver set, requests and withdraw,
 * the approvals queue with its detail, decisions (single and bulk) and cancel. Every mutation
 * invalidates the balances, the approvals, Home's tasks and the bell so each screen redraws
 * from the API's answer.
 */
import {
  ApprovalDetailSchema,
  ApprovalHistoryResponseSchema,
  DisagreementActionResponseSchema,
  HolidayAdjustmentResponseSchema,
  HolidayAdminPeopleResponseSchema,
  HolidayAdminYearSchema,
  SiteHolidaySettingsSchema,
  ApprovalsResponseSchema,
  ApproversResponseSchema,
  BankHolidaysResponseSchema,
  BulkApproveResponseSchema,
  HolidayCalendarResponseSchema,
  HolidayDecisionResponseSchema,
  HolidayDisagreementsResponseSchema,
  HolidayRequestResponseSchema,
  HolidaySummarySchema,
  OverrideDeleteResponseSchema,
  OverrideResponseSchema,
  SaturdayRotaResponseSchema,
  SaturdayRotaSaveResponseSchema,
  StaleWithdrawResponseSchema,
  WorkingPatternResponseSchema,
  BalancesReportSchema,
  BookingsByMonthReportSchema,
  ExceptionsReportSchema,
  HolidayPrefsSchema,
  LiabilityReportSchema,
  NothingBookedReportSchema,
  YearEndApplyResponseSchema,
  YearEndResponseSchema,
  type BalancesReport,
  type BookingsByMonthReport,
  type ExceptionsReport,
  type HolidayPrefs,
  type HolidayPrefsPatch,
  type LiabilityReport,
  type NothingBookedReport,
  type YearEndApply,
  type YearEndApplyResponse,
  type YearEndResponse,
  type ApprovalDetail,
  type ApprovalHistoryResponse,
  type DisagreementActionResponse,
  type HolidayAdjustmentCreate,
  type HolidayAdjustmentResponse,
  type HolidayAdminPeopleQuery,
  type HolidayAdminPeopleResponse,
  type HolidayAdminYear,
  type HolidayYearPut,
  type SiteHolidaySettings,
  type SiteHolidaySettingsPut,
  type WorkingPatternPut,
  type ApprovalsResponse,
  type ApproversResponse,
  type BankHolidaysResponse,
  type BulkApproveResponse,
  type HolidayCalendarResponse,
  type HolidayDecisionResponse,
  type HolidayDisagreementsResponse,
  type HolidayRequestCreate,
  type HolidayRequestResponse,
  type HolidaySummary,
  type OverrideCreate,
  type OverrideDeleteResponse,
  type OverrideResponse,
  type SaturdayRotaPut,
  type SaturdayRotaResponse,
  type SaturdayRotaSaveResponse,
  type StaleWithdrawResponse,
  type WorkingPatternResponse,
} from '@aroo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from './client';
import { notificationKeys } from './notifications';

const enc = encodeURIComponent;

export const holidayKeys = {
  me: (year: number | null) => ['holidays', 'me', year] as const,
  person: (personId: string, year: number | null) =>
    ['holidays', 'person', personId, year] as const,
  bankHolidays: (year: number | null) => ['holidays', 'bank-holidays', year] as const,
  disagreements: (year: number | null) => ['holidays', 'disagreements', year] as const,
  calendar: (site: string, month: string) => ['holidays', 'calendar', site, month] as const,
  approvers: (personId: string) => ['holidays', 'approvers', personId] as const,
  approvals: ['holidays', 'approvals'] as const,
  approval: (id: string) => ['holidays', 'approvals', 'detail', id] as const,
  history: ['holidays', 'approvals', 'history'] as const,
  pattern: (personId: string) => ['holidays', 'pattern', personId] as const,
  rota: (site: string) => ['holidays', 'rota', site] as const,
  // M3
  adminPeople: (q: HolidayAdminPeopleQuery) => ['holidays', 'admin', 'people', q] as const,
  adminYear: (personId: string, year: number) =>
    ['holidays', 'admin', 'year', personId, year] as const,
  adminDisagreements: (year: number | null, includeResolved: boolean) =>
    ['holidays', 'admin', 'disagreements', year, includeResolved] as const,
  siteSettings: (code: string) => ['holidays', 'site-settings', code] as const,
  // M4
  yearEnd: (from: number, to: number) => ['holidays', 'admin', 'year-end', from, to] as const,
  report: (kind: string, q: object) => ['holidays', 'reports', kind, q] as const,
  prefs: ['holidays', 'me', 'prefs'] as const,
};

const yearQuery = (year: number | null) => (year === null ? undefined : { year });

export function getMyHolidays(year: number | null): Promise<HolidaySummary> {
  return apiGet('/holidays/me', HolidaySummarySchema, yearQuery(year));
}

export function getPersonHolidays(personId: string, year: number | null): Promise<HolidaySummary> {
  return apiGet(`/holidays/people/${enc(personId)}`, HolidaySummarySchema, yearQuery(year));
}

export function getBankHolidays(year: number | null): Promise<BankHolidaysResponse> {
  return apiGet('/holidays/bank-holidays', BankHolidaysResponseSchema, yearQuery(year));
}

export function getDisagreements(year: number | null): Promise<HolidayDisagreementsResponse> {
  return apiGet(
    '/holidays/queues/disagreements',
    HolidayDisagreementsResponseSchema,
    yearQuery(year),
  );
}

/** The signed-in person's (or the viewed-as person's) holiday year; null = the current year. */
export function useMyHolidays(year: number | null = null, enabled = true) {
  return useQuery({ queryKey: holidayKeys.me(year), queryFn: () => getMyHolidays(year), enabled });
}

export function usePersonHolidays(personId: string, year: number | null = null, enabled = true) {
  return useQuery({
    queryKey: holidayKeys.person(personId, year),
    queryFn: () => getPersonHolidays(personId, year),
    enabled: enabled && personId.length > 0,
  });
}

export function useBankHolidays(year: number | null = null) {
  return useQuery({
    queryKey: holidayKeys.bankHolidays(year),
    queryFn: () => getBankHolidays(year),
  });
}

export function useHolidayDisagreements(year: number | null = null, enabled = true) {
  return useQuery({
    queryKey: holidayKeys.disagreements(year),
    queryFn: () => getDisagreements(year),
    enabled,
  });
}

// M2 --------------------------------------------------------------------------------------

export function getCalendar(site: string, month: string): Promise<HolidayCalendarResponse> {
  return apiGet('/holidays/calendar', HolidayCalendarResponseSchema, { site, month });
}

/** M3: a site or a region for a month, optionally one job title. */
export interface CalendarScopeQuery {
  kind: 'site' | 'region';
  code: string;
}

export function getScopeCalendar(
  scope: CalendarScopeQuery,
  month: string,
  role: string | null,
): Promise<HolidayCalendarResponse> {
  return apiGet('/holidays/calendar', HolidayCalendarResponseSchema, {
    ...(scope.kind === 'site' ? { site: scope.code } : { region: scope.code }),
    month,
    ...(role ? { role } : {}),
  });
}

export function useScopeCalendar(
  scope: CalendarScopeQuery | null,
  month: string,
  role: string | null,
) {
  return useQuery({
    queryKey: [
      'holidays',
      'calendar',
      scope?.kind ?? '',
      scope?.code ?? '',
      month,
      role ?? '',
    ] as const,
    queryFn: () => getScopeCalendar(scope!, month, role),
    enabled: scope !== null,
    retry: false,
    staleTime: 60_000,
  });
}

/** The branch calendar for a month; 403 when the branch keeps it to managers (caller decides). */
export function useBranchCalendar(site: string | null, month: string | null) {
  return useQuery({
    queryKey: holidayKeys.calendar(site ?? '', month ?? ''),
    queryFn: () => getCalendar(site ?? '', month ?? ''),
    enabled: site !== null && site.length > 0 && month !== null,
    retry: false,
    staleTime: 60_000,
  });
}

export function getApprovers(personId: string): Promise<ApproversResponse> {
  return apiGet(`/holidays/approvers/${enc(personId)}`, ApproversResponseSchema);
}

export function useApprovers(personId: string, enabled = true) {
  return useQuery({
    queryKey: holidayKeys.approvers(personId),
    queryFn: () => getApprovers(personId),
    enabled: enabled && personId.length > 0,
    staleTime: 5 * 60_000,
  });
}

export function createHolidayRequest(body: HolidayRequestCreate): Promise<HolidayRequestResponse> {
  return apiPost('/holidays/requests', HolidayRequestResponseSchema, body);
}

export function withdrawHolidayRequest(id: string): Promise<HolidayDecisionResponse> {
  return apiPost(`/holidays/requests/${enc(id)}/withdraw`, HolidayDecisionResponseSchema);
}

export function bookOnBehalf(
  personId: string,
  body: HolidayRequestCreate,
): Promise<HolidayRequestResponse> {
  return apiPost(`/holidays/people/${enc(personId)}/book`, HolidayRequestResponseSchema, body);
}

export function getApprovals(): Promise<ApprovalsResponse> {
  return apiGet('/holidays/approvals', ApprovalsResponseSchema);
}

export function getApprovalDetail(id: string): Promise<ApprovalDetail> {
  return apiGet(`/holidays/approvals/${enc(id)}`, ApprovalDetailSchema);
}

export function approveRequest(id: string, note?: string): Promise<HolidayDecisionResponse> {
  return apiPost(`/holidays/approvals/${enc(id)}/approve`, HolidayDecisionResponseSchema, {
    ...(note ? { note } : {}),
  });
}

export function declineRequest(id: string, note: string): Promise<HolidayDecisionResponse> {
  return apiPost(`/holidays/approvals/${enc(id)}/decline`, HolidayDecisionResponseSchema, {
    note,
  });
}

export function bulkApproveRequests(ids: readonly string[]): Promise<BulkApproveResponse> {
  return apiPost('/holidays/approvals/bulk-approve', BulkApproveResponseSchema, { ids });
}

export function cancelBooking(id: string, reason: string): Promise<HolidayDecisionResponse> {
  return apiPost(`/holidays/bookings/${enc(id)}/cancel`, HolidayDecisionResponseSchema, {
    reason,
  });
}

/** Pending requests the signed-in person may decide (oldest first). Off for non-approvers. */
export function useApprovals(enabled = true) {
  return useQuery({
    queryKey: holidayKeys.approvals,
    queryFn: getApprovals,
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useApprovalDetail(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: holidayKeys.approval(id ?? ''),
    queryFn: () => getApprovalDetail(id ?? ''),
    enabled: enabled && typeof id === 'string' && id.length > 0,
    retry: false,
  });
}

/** Everything a holiday change can move: balances, approvals, Home's tasks, the bell. */
export function useInvalidateHolidays() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['holidays'] });
    void qc.invalidateQueries({ queryKey: ['home', 'tasks'] });
    void qc.invalidateQueries({ queryKey: notificationKeys.list });
  };
}

export function useCreateHolidayRequest() {
  const invalidate = useInvalidateHolidays();
  return useMutation({ mutationFn: createHolidayRequest, onSuccess: invalidate });
}

export function useWithdrawHolidayRequest() {
  const invalidate = useInvalidateHolidays();
  return useMutation({ mutationFn: withdrawHolidayRequest, onSuccess: invalidate });
}

export function useApproveRequest() {
  const invalidate = useInvalidateHolidays();
  return useMutation({
    mutationFn: (v: { id: string; note?: string }) => approveRequest(v.id, v.note),
    onSuccess: invalidate,
    onError: invalidate,
  });
}

export function useDeclineRequest() {
  const invalidate = useInvalidateHolidays();
  return useMutation({
    mutationFn: (v: { id: string; note: string }) => declineRequest(v.id, v.note),
    onSuccess: invalidate,
    onError: invalidate,
  });
}

export function useBulkApprove() {
  const invalidate = useInvalidateHolidays();
  return useMutation({ mutationFn: bulkApproveRequests, onSuccess: invalidate });
}

export function useCancelBooking() {
  const invalidate = useInvalidateHolidays();
  return useMutation({
    mutationFn: (v: { id: string; reason: string }) => cancelBooking(v.id, v.reason),
    onSuccess: invalidate,
  });
}

// M2b: Saturday rota, working days, swaps, stale pending, approvals history ----------------

export function getApprovalHistory(): Promise<ApprovalHistoryResponse> {
  return apiGet('/holidays/approvals/history', ApprovalHistoryResponseSchema);
}

export function useApprovalHistory(enabled = true) {
  return useQuery({
    queryKey: holidayKeys.history,
    queryFn: getApprovalHistory,
    enabled,
    staleTime: 30_000,
  });
}

export function getPersonPattern(personId: string): Promise<WorkingPatternResponse> {
  return apiGet(`/holidays/people/${enc(personId)}/pattern`, WorkingPatternResponseSchema);
}

/** The person's patterns and overrides (managers at their site, HR); 403 otherwise. */
export function usePersonPattern(personId: string, enabled = true) {
  return useQuery({
    queryKey: holidayKeys.pattern(personId),
    queryFn: () => getPersonPattern(personId),
    enabled: enabled && personId.length > 0,
    retry: false,
  });
}

export function getSaturdayRota(site: string): Promise<SaturdayRotaResponse> {
  return apiGet(`/holidays/sites/${enc(site)}/saturday-rota`, SaturdayRotaResponseSchema);
}

export function saveSaturdayRota(
  site: string,
  body: SaturdayRotaPut,
): Promise<SaturdayRotaSaveResponse> {
  return apiPut(`/holidays/sites/${enc(site)}/saturday-rota`, SaturdayRotaSaveResponseSchema, body);
}

export function useSaturdayRota(site: string | null) {
  return useQuery({
    queryKey: holidayKeys.rota(site ?? ''),
    queryFn: () => getSaturdayRota(site ?? ''),
    enabled: site !== null && site.length > 0,
    retry: false,
    // Unsaved chip choices live on the page; a focus refetch must not reset them.
    refetchOnWindowFocus: false,
  });
}

export function useSaveSaturdayRota() {
  const invalidate = useInvalidateHolidays();
  return useMutation({
    mutationFn: (v: { site: string; body: SaturdayRotaPut }) => saveSaturdayRota(v.site, v.body),
    onSuccess: invalidate,
  });
}

export function createOverride(personId: string, body: OverrideCreate): Promise<OverrideResponse> {
  return apiPost(`/holidays/people/${enc(personId)}/overrides`, OverrideResponseSchema, body);
}

export function deleteOverride(id: string): Promise<OverrideDeleteResponse> {
  return apiDelete(`/holidays/overrides/${enc(id)}`, OverrideDeleteResponseSchema);
}

export function useCreateOverride() {
  const invalidate = useInvalidateHolidays();
  return useMutation({
    mutationFn: (v: { personId: string; body: OverrideCreate }) =>
      createOverride(v.personId, v.body),
    onSuccess: invalidate,
  });
}

export function useDeleteOverride() {
  const invalidate = useInvalidateHolidays();
  return useMutation({ mutationFn: deleteOverride, onSuccess: invalidate });
}

export function useBookOnBehalf() {
  const invalidate = useInvalidateHolidays();
  return useMutation({
    mutationFn: (v: { personId: string; body: HolidayRequestCreate }) =>
      bookOnBehalf(v.personId, v.body),
    onSuccess: invalidate,
  });
}

export function withdrawStalePending(): Promise<StaleWithdrawResponse> {
  return apiPost('/holidays/admin/stale-pending/withdraw', StaleWithdrawResponseSchema);
}

export function useWithdrawStalePending() {
  const qc = useQueryClient();
  const invalidate = useInvalidateHolidays();
  return useMutation({
    mutationFn: withdrawStalePending,
    onSuccess: () => {
      invalidate();
      void qc.invalidateQueries({ queryKey: ['queues'] });
    },
  });
}

// M3: HR admin, full pattern editor, disagreements, site settings -------------------------

export function getAdminPeople(q: HolidayAdminPeopleQuery): Promise<HolidayAdminPeopleResponse> {
  return apiGet('/holidays/admin/people', HolidayAdminPeopleResponseSchema, {
    ...(q.year ? { year: q.year } : {}),
    ...(q.site ? { site: q.site } : {}),
    ...(q.region ? { region: q.region } : {}),
    ...(q.q ? { q: q.q } : {}),
  });
}

export function useAdminPeople(q: HolidayAdminPeopleQuery, enabled = true) {
  return useQuery({
    queryKey: holidayKeys.adminPeople(q),
    queryFn: () => getAdminPeople(q),
    enabled,
    staleTime: 30_000,
  });
}

export function getAdminYear(personId: string, year: number): Promise<HolidayAdminYear> {
  return apiGet(`/holidays/admin/people/${enc(personId)}/year/${year}`, HolidayAdminYearSchema);
}

export function useAdminYear(personId: string | null, year: number) {
  return useQuery({
    queryKey: holidayKeys.adminYear(personId ?? '', year),
    queryFn: () => getAdminYear(personId ?? '', year),
    enabled: personId !== null,
    retry: false,
  });
}

export function setAdminYear(
  personId: string,
  year: number,
  body: HolidayYearPut,
): Promise<HolidayAdminYear> {
  return apiPut(
    `/holidays/admin/people/${enc(personId)}/year/${year}`,
    HolidayAdminYearSchema,
    body,
  );
}

export function useSetAdminYear() {
  const invalidate = useInvalidateHolidays();
  return useMutation({
    mutationFn: (v: { personId: string; year: number; body: HolidayYearPut }) =>
      setAdminYear(v.personId, v.year, v.body),
    onSuccess: invalidate,
  });
}

export function addAdjustment(
  personId: string,
  body: HolidayAdjustmentCreate,
): Promise<HolidayAdjustmentResponse> {
  return apiPost(
    `/holidays/admin/people/${enc(personId)}/adjustments`,
    HolidayAdjustmentResponseSchema,
    body,
  );
}

export function reverseAdjustment(id: string): Promise<HolidayAdjustmentResponse> {
  return apiDelete(`/holidays/admin/adjustments/${enc(id)}`, HolidayAdjustmentResponseSchema);
}

export function useAddAdjustment() {
  const invalidate = useInvalidateHolidays();
  return useMutation({
    mutationFn: (v: { personId: string; body: HolidayAdjustmentCreate }) =>
      addAdjustment(v.personId, v.body),
    onSuccess: invalidate,
  });
}

export function useReverseAdjustment() {
  const invalidate = useInvalidateHolidays();
  return useMutation({ mutationFn: reverseAdjustment, onSuccess: invalidate });
}

export function getFullPattern(personId: string): Promise<WorkingPatternResponse> {
  return apiGet(
    `/holidays/admin/people/${enc(personId)}/pattern/full`,
    WorkingPatternResponseSchema,
  );
}

export function useFullPattern(personId: string | null) {
  return useQuery({
    queryKey: ['holidays', 'admin', 'pattern', personId ?? ''] as const,
    queryFn: () => getFullPattern(personId ?? ''),
    enabled: personId !== null,
    retry: false,
  });
}

export function putFullPattern(
  personId: string,
  body: WorkingPatternPut,
): Promise<WorkingPatternResponse> {
  return apiPut(
    `/holidays/admin/people/${enc(personId)}/pattern/full`,
    WorkingPatternResponseSchema,
    body,
  );
}

export function usePutFullPattern() {
  const invalidate = useInvalidateHolidays();
  return useMutation({
    mutationFn: (v: { personId: string; body: WorkingPatternPut }) =>
      putFullPattern(v.personId, v.body),
    onSuccess: invalidate,
  });
}

export function getAdminDisagreements(
  year: number | null,
  includeResolved: boolean,
): Promise<HolidayDisagreementsResponse> {
  return apiGet('/holidays/admin/disagreements', HolidayDisagreementsResponseSchema, {
    ...(year ? { year } : {}),
    includeResolved: includeResolved ? 'true' : 'false',
  });
}

export function useAdminDisagreements(
  year: number | null,
  includeResolved: boolean,
  enabled = true,
) {
  return useQuery({
    queryKey: holidayKeys.adminDisagreements(year, includeResolved),
    queryFn: () => getAdminDisagreements(year, includeResolved),
    enabled,
    staleTime: 30_000,
  });
}

export function resolveDisagreement(
  id: string,
  action: 'accept' | 'adjust',
): Promise<DisagreementActionResponse> {
  return apiPost(
    `/holidays/admin/disagreements/${enc(id)}/${action}`,
    DisagreementActionResponseSchema,
  );
}

export function useResolveDisagreement() {
  const qc = useQueryClient();
  const invalidate = useInvalidateHolidays();
  return useMutation({
    mutationFn: (v: { id: string; action: 'accept' | 'adjust' }) =>
      resolveDisagreement(v.id, v.action),
    onSuccess: () => {
      invalidate();
      void qc.invalidateQueries({ queryKey: ['queues'] });
    },
  });
}

export function getSiteSettings(code: string): Promise<SiteHolidaySettings> {
  return apiGet(`/holidays/sites/${enc(code)}/settings`, SiteHolidaySettingsSchema);
}

export function useSiteHolidaySettings(code: string | null, enabled = true) {
  return useQuery({
    queryKey: holidayKeys.siteSettings(code ?? ''),
    queryFn: () => getSiteSettings(code ?? ''),
    enabled: enabled && code !== null,
    retry: false,
  });
}

export function putSiteSettings(
  code: string,
  body: SiteHolidaySettingsPut,
): Promise<SiteHolidaySettings> {
  return apiPut(`/holidays/sites/${enc(code)}/settings`, SiteHolidaySettingsSchema, body);
}

export function usePutSiteSettings() {
  const qc = useQueryClient();
  const invalidate = useInvalidateHolidays();
  return useMutation({
    mutationFn: (v: { code: string; body: SiteHolidaySettingsPut }) =>
      putSiteSettings(v.code, v.body),
    onSuccess: (settings) => {
      qc.setQueryData(holidayKeys.siteSettings(settings.site.code), settings);
      invalidate();
    },
  });
}

// M4: year-end tool ---------------------------------------------------------------------------

export function getYearEnd(from: number, to: number): Promise<YearEndResponse> {
  return apiGet('/holidays/admin/year-end', YearEndResponseSchema, { from, to });
}

export function useYearEnd(from: number, to: number, enabled = true) {
  return useQuery({
    queryKey: holidayKeys.yearEnd(from, to),
    queryFn: () => getYearEnd(from, to),
    enabled,
  });
}

export function applyYearEnd(body: YearEndApply): Promise<YearEndApplyResponse> {
  return apiPost('/holidays/admin/year-end/apply', YearEndApplyResponseSchema, body);
}

export function useApplyYearEnd() {
  const qc = useQueryClient();
  const invalidate = useInvalidateHolidays();
  return useMutation({
    mutationFn: applyYearEnd,
    onSuccess: () => {
      invalidate();
      void qc.invalidateQueries({ queryKey: ['holidays', 'admin'] });
    },
  });
}

// M4: reports ------------------------------------------------------------------------------------

export interface ReportQuery {
  year: number;
  site?: string | undefined;
  region?: string | undefined;
}

export interface NothingBookedReportQuery extends ReportQuery {
  minDays?: number | undefined;
  asOf?: string | undefined;
}

const reportQuery = (q: ReportQuery) => ({
  year: q.year,
  ...(q.site ? { site: q.site } : {}),
  ...(q.region ? { region: q.region } : {}),
});

export function getBalancesReport(q: ReportQuery): Promise<BalancesReport> {
  return apiGet('/holidays/reports/balances', BalancesReportSchema, reportQuery(q));
}

export function getLiabilityReport(q: ReportQuery): Promise<LiabilityReport> {
  return apiGet('/holidays/reports/liability', LiabilityReportSchema, reportQuery(q));
}

export function getBookingsByMonthReport(q: ReportQuery): Promise<BookingsByMonthReport> {
  return apiGet('/holidays/reports/bookings-by-month', BookingsByMonthReportSchema, reportQuery(q));
}

export function getNothingBookedReport(q: NothingBookedReportQuery): Promise<NothingBookedReport> {
  return apiGet('/holidays/reports/nothing-booked', NothingBookedReportSchema, {
    ...reportQuery(q),
    ...(q.minDays !== undefined ? { minDays: q.minDays } : {}),
    ...(q.asOf ? { asOf: q.asOf } : {}),
  });
}

export function getExceptionsReport(q: ReportQuery): Promise<ExceptionsReport> {
  return apiGet('/holidays/reports/exceptions', ExceptionsReportSchema, reportQuery(q));
}

export function useBalancesReport(q: ReportQuery, enabled = true) {
  return useQuery({
    queryKey: holidayKeys.report('balances', q),
    queryFn: () => getBalancesReport(q),
    enabled,
  });
}

export function useLiabilityReport(q: ReportQuery, enabled = true) {
  return useQuery({
    queryKey: holidayKeys.report('liability', q),
    queryFn: () => getLiabilityReport(q),
    enabled,
  });
}

export function useBookingsByMonthReport(q: ReportQuery, enabled = true) {
  return useQuery({
    queryKey: holidayKeys.report('bookings-by-month', q),
    queryFn: () => getBookingsByMonthReport(q),
    enabled,
  });
}

export function useNothingBookedReport(q: NothingBookedReportQuery, enabled = true) {
  return useQuery({
    queryKey: holidayKeys.report('nothing-booked', q),
    queryFn: () => getNothingBookedReport(q),
    enabled,
  });
}

export function useExceptionsReport(q: ReportQuery, enabled = true) {
  return useQuery({
    queryKey: holidayKeys.report('exceptions', q),
    queryFn: () => getExceptionsReport(q),
    enabled,
  });
}

// M4: the daily digest opt-out ---------------------------------------------------------------

export function getMyHolidayPrefs(): Promise<HolidayPrefs> {
  return apiGet('/holidays/me/prefs', HolidayPrefsSchema);
}

export function patchMyHolidayPrefs(body: HolidayPrefsPatch): Promise<HolidayPrefs> {
  return apiPatch('/holidays/me/prefs', HolidayPrefsSchema, body);
}

export function useMyHolidayPrefs(enabled = true) {
  return useQuery({ queryKey: holidayKeys.prefs, queryFn: getMyHolidayPrefs, enabled });
}

export function useSetMyHolidayPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: patchMyHolidayPrefs,
    onSuccess: (prefs) => qc.setQueryData(holidayKeys.prefs, prefs),
  });
}
